import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useModuleNav } from '../../contexts/ModuleNavContext';
import { useGuardedLogout } from '../../hooks/useGuardedLogout';
import { groupItemsByGroup } from './groupItems';
import { moduleMetaForPath } from './moduleMeta';
import { iconForLabelKey } from './itemIcons';
import SidebarItem from './SidebarItem';
import SidebarGroup from './SidebarGroup';
import ModuleSwitcher from './ModuleSwitcher';
import HomeModuleList from './HomeModuleList';
import LanguageSwitcher from '../LanguageSwitcher';
import './AppShell.css';

export interface SidebarProps {
  /** Called when the user picks a nav item — used by the phone drawer to close itself. */
  onCloseMobile: () => void;
  /** True when the sidebar is rendered in 56px tablet icon-rail mode. */
  collapsed?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ onCloseMobile, collapsed }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { navItems } = useModuleNav();
  const { user } = useAuth();
  const { requestLogout, confirm: signOutConfirm } = useGuardedLogout();

  const moduleMeta = useMemo(() => moduleMetaForPath(pathname), [pathname]);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const grouped = useMemo(() => groupItemsByGroup(navItems), [navItems]);

  const handleLogout = () => {
    setUserMenuOpen(false);
    // The phone drawer stays open so the sign-out warning (kept in this
    // component) is not unmounted with it; signing out replaces the shell anyway.
    requestLogout();
  };

  const initials = (user?.name ?? 'U')
    .split(/\s+/)
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <aside className={`shell-sidebar${collapsed ? ' shell-sidebar--collapsed' : ''}`} aria-label={t('shell.sidebarLabel')}>
      {/* Brand row */}
      <Link to="/" onClick={onCloseMobile} className="shell-sidebar__brand">
        <span className="shell-sidebar__brand-mark" aria-hidden="true">
          <i className="bi bi-droplet-fill" />
        </span>
        {!collapsed && <span className="shell-sidebar__brand-text">Citrus Synch</span>}
      </Link>

      {/* Module pill — only rendered when a module is active. The pill is
          the trigger for the popover module switcher; on non-module routes
          (Home, etc.) the sidebar instead surfaces the full module list
          directly so navigation is one click, not two. */}
      {moduleMeta && (
        <>
          {!collapsed && <div className="shell-sidebar__module-label">{t('shell.moduleLabel')}</div>}
          <button
            className="shell-sidebar__module-pill"
            data-testid="module-pill"
            onClick={() => setSwitcherOpen(o => !o)}
            aria-expanded={switcherOpen}
            aria-label={t('shell.switchModule')}
          >
            <span className="shell-sidebar__module-pill-icon" aria-hidden="true">{moduleMeta.emoji}</span>
            {!collapsed && <span className="shell-sidebar__module-pill-text">{t(moduleMeta.labelKey)}</span>}
            {!collapsed && <i className="bi bi-chevron-down shell-sidebar__module-pill-caret" aria-hidden="true" />}
          </button>
          {switcherOpen && (
            <ModuleSwitcher
              onClose={() => setSwitcherOpen(false)}
              onCloseMobile={onCloseMobile}
            />
          )}
        </>
      )}

      {/* Middle section — either the active module's sub-nav, or (on Home and
          other non-module routes) a direct module list for one-click access. */}
      {moduleMeta ? (
        <nav className="shell-sidebar__nav">
          {grouped.map(group => (
            <SidebarGroup
              key={group.groupKey ?? 'ungrouped'}
              labelKey={group.groupKey ?? 'nav.groups.overview'}
              collapsed={collapsed}
            >
              {group.items.map(item => (
                <SidebarItem
                  key={item.to}
                  to={item.to}
                  labelKey={item.labelKey}
                  icon={iconForLabelKey(item.labelKey)}
                  end={item.end}
                  onNavigate={onCloseMobile}
                />
              ))}
            </SidebarGroup>
          ))}
        </nav>
      ) : (
        <HomeModuleList collapsed={collapsed} onNavigate={onCloseMobile} />
      )}

      {/* User row pinned to the bottom */}
      <div className="shell-sidebar__spacer" />
      <div className="shell-sidebar__user">
        <button
          className="shell-sidebar__user-row"
          onClick={() => setUserMenuOpen(o => !o)}
          aria-expanded={userMenuOpen}
          aria-label={t('userMenu.openLabel')}
        >
          <span className="shell-sidebar__avatar">{initials}</span>
          {!collapsed && (
            <span className="shell-sidebar__user-text">
              <span className="shell-sidebar__user-name">{user?.name ?? '—'}</span>
              <span className="shell-sidebar__user-role">{user?.role?.name ?? ''}</span>
            </span>
          )}
        </button>
        {userMenuOpen && (
          <div className="shell-sidebar__user-menu" role="menu">
            <div className="shell-sidebar__user-menu-row">
              <span className="shell-sidebar__user-menu-label">{t('userMenu.language')}</span>
              <LanguageSwitcher />
            </div>
            <Link
              to="/phone-check"
              className="shell-sidebar__user-menu-action"
              role="menuitem"
              onClick={() => { setUserMenuOpen(false); onCloseMobile(); }}
            >
              <i className="bi bi-phone" aria-hidden="true" /> {t('userMenu.phoneCheck')}
            </Link>
            <button className="shell-sidebar__user-menu-action" onClick={handleLogout} role="menuitem">
              <i className="bi bi-box-arrow-right" aria-hidden="true" /> {t('userMenu.logout')}
            </button>
          </div>
        )}
      </div>
      {signOutConfirm}
    </aside>
  );
};

export default Sidebar;
